<template>
  <f7-page name="shop-cart">
    <f7-navbar :title="$t('shopPage.myCart')" back-link>
      <f7-nav-right>
        <f7-link icon-f7="square_arrow_up" @click="shareOpen = true" />
      </f7-nav-right>
    </f7-navbar>
<AppLayout>
  <template #main>
    

    <f7-block v-if="loading" class="text-align-center">
      <f7-preloader />
    </f7-block>

    <template v-else-if="cart">
      <div v-if="cart.items?.length" class="share-banner">
        <f7-icon f7="link" size="12" />
        {{ $t('shopPage.shareableCart') }}
      </div>

      <!-- Cart Items -->
      <div class="item-list">
        <div v-for="item in cart.items" :key="item.id" class="cart-item">
          <div class="item-img">
            <img v-if="item.imageUrl" :src="item.imageUrl" />
            <f7-icon v-else f7="bag_fill" size="18" color="#8e8e93" />
          </div>
          <div class="item-body">
            <div class="item-name">{{ item.productName }}</div>
            <div class="item-variant" v-if="item.variantName">{{ item.variantName }}</div>
            <div class="item-price">{{ formatPrice(item.unitPrice) }}</div>
          </div>
          <div class="item-qty">
            <f7-stepper
              :min="1"
              :max="99"
              :value="item.quantity"
              @stepper:change="(val) => updateQty(item.id, val)"
            />
          </div>
          <f7-link icon-f7="trash" color="red" @click="removeItem(item.id)" />
        </div>
      </div>

      <div v-if="!cart.items?.length" class="empty-hint">
        <f7-icon f7="bag_x_fill" size="28" color="#8e8e93" />
        <div>{{ $t('shop.panierVide') }}</div>
      </div>

      <!-- Summary -->
      <div class="summary-block">
        <div class="summary-row">
          <span>{{ $t('shop.sousTotal') }}</span>
          <span>{{ formatPrice(cart.subtotal) }}</span>
        </div>
        <div class="summary-row">
          <span>{{ $t('shop.livraison') }}</span>
          <span>{{ formatPrice(cart.deliveryFee || 0) }}</span>
        </div>
        <div class="summary-total">
          <span>{{ $t('shop.total') }}</span>
          <span>{{ formatPrice((cart.subtotal || 0) + (cart.deliveryFee || 0)) }}</span>
        </div>
      </div>

      <!-- Delivery Options -->
      <div class="section-title">{{ $t('shop.livraison') }}</div>
      <f7-list inset strong>
        <f7-list-item
          radio radio-icon="start"
          :title="$t('shop.livraisonLocale')"
          name="delivery"
          :checked="deliveryOption === 'LOCAL_DELIVERY'"
          @change="deliveryOption = 'LOCAL_DELIVERY'"
        />
        <f7-list-item
          radio radio-icon="start"
          :title="$t('shop.retraitBoutique')"
          name="delivery"
          :checked="deliveryOption === 'PICKUP'"
          @change="deliveryOption = 'PICKUP'"
        />
        <f7-list-item
          radio radio-icon="start"
          :title="$t('shop.livraisonNationale')"
          name="delivery"
          :checked="deliveryOption === 'NATIONAL_DELIVERY'"
          @change="deliveryOption = 'NATIONAL_DELIVERY'"
        />
      </f7-list>

      <!-- Payment Methods -->
      <div class="section-title">{{ $t('shop.paiement') }}</div>
      <f7-list inset strong>
        <f7-list-item
          v-for="method in paymentMethods"
          :key="method.code"
          radio radio-icon="start"
          :title="method.label"
          name="payment"
          :checked="paymentMethod === method.code"
          @change="paymentMethod = method.code"
        />
      </f7-list>

      <!-- Checkout -->
      <f7-block>
        <f7-button large fill raised round :disabled="!cart.items?.length" @click="goToCheckout">
          {{ `${$t('shopPage.confirm')} — ${formatPrice((cart.subtotal || 0) + (cart.deliveryFee || 0))}` }}
        </f7-button>
      </f7-block>
    </template>

    <div v-else class="empty-hint">
      <f7-icon f7="bag_x_fill" size="28" color="#8e8e93" />
      <div>{{ $t('shopPage.cartNotFound') }}</div>
    </div>

  </template>
</AppLayout>
    <!-- Share Sheet -->
    <AdaptiveSheet v-model:opened="shareOpen"  swipe-to-close close-by-outside-click close-on-escape tablet-fullscreen="false">
      <div class="padding">
        <div class="text-align-center" style="font-weight: 600; margin-bottom: 12px;">{{ $t('shopPage.shareCart') }}</div>
        <f7-button large outline raised round @click="copyShareLink">
          <f7-icon f7="doc_on_doc" size="14" /> {{ $t('shopPage.copyLink') }}
        </f7-button>
        <f7-button large outline raised round class="margin-top" @click="shareToChat">
          <f7-icon f7="chat_bubble_2_fill" size="14" /> {{ $t('shopPage.shareToChat') }}
        </f7-button>
      </div>
    </AdaptiveSheet>
  </f7-page>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { f7 } from 'framework7-vue'
import AppLayout from '@/components/common/AppLayout.vue'
import AdaptiveSheet from '../components/common/AdaptiveSheet.vue'
import { useCartStore } from '@/js/pinia-stores/cart.js'
import { useAuthStore } from '@/js/pinia-stores/auth.js'
import i18n from '../js/i18n.js'

const { t } = i18n.global
const props = defineProps({
  cartId: { type: String, default: null },
  f7route: Object,
  f7router: Object,
})

const authStore = useAuthStore()
const cartStore = useCartStore()

const loading = ref(true)
const cart = ref(null)
const shareOpen = ref(false)
const deliveryOption = ref('LOCAL_DELIVERY')
const paymentMethod = ref('AVO_WALLET')

const paymentMethods = [
  { code: 'AVO_WALLET', label: 'Avo Wallet' },
  { code: 'WAVE', label: 'Wave' },
  { code: 'ORANGE_MONEY', label: 'Orange Money' },
]

onMounted(async () => {
  try {
    if (props.cartId) {
      const data = await cartStore.get(props.cartId)
      cart.value = data
    } else {
      const userId = authStore.profile?.sub
      if (userId) {
        const userCarts = await cartStore.getByUser(userId).catch(() => [])
        cart.value = userCarts.find((c) => c.status === 'OPEN') || userCarts[0] || null
      }
    }
  } catch {
    cart.value = null
  } finally {
    loading.value = false
  }
})

async function updateQty(itemId, qty) {
  if (!cart.value) return
  try {
    await cartStore.updateItem(itemId, { quantity: qty })
    const item = cart.value.items?.find((i) => i.id === itemId)
    if (item) item.quantity = qty
  } catch {
    f7.toast.show({ text: t('shop.erreurMiseAJour'), closeTimeout: 1500 })
  }
}

async function removeItem(itemId) {
  if (!cart.value) return
  try {
    await cartStore.removeItem(itemId)
    cart.value.items = (cart.value.items || []).filter((i) => i.id !== itemId)
    f7.toast.show({ text: t('shopPage.articleSupprime'), closeTimeout: 1500 })
  } catch {
    f7.toast.show({ text: t('shop.erreurSuppression'), closeTimeout: 1500 })
  }
}

/**
 * Hand off to the dedicated checkout page (delivery + address + payment
 * initiation) instead of checking out inline.
 */
function goToCheckout() {
  if (!cart.value?.id) return;
  props.f7router.navigate(`/checkout/${cart.value.id}`);
}

function copyShareLink() {
  const url = `${window.location.origin}/shared-cart/${cart.value?.id || ''}`
  navigator.clipboard?.writeText(url)
  shareOpen.value = false
  f7.toast.show({ text: t('shopPage.lienCopie'), closeTimeout: 1500 })
}

function shareToChat() {
  shareOpen.value = false
  f7.toast.show({ text: t('shopPage.chatSoon'), closeTimeout: 2000 })
}

function formatPrice(amount) {
  return `${Math.round(amount || 0).toLocaleString('fr-FR')} CFA`
}
</script>

<style scoped>
.share-banner {
  display: flex; align-items: center; gap: 6px; font-size: 10.5px;
  color: var(--f7-on-surface-variant, #8e8e93); background: var(--f7-list-bg-color, #f5f5f5);
  border-radius: 8px; padding: 8px 12px; margin: 8px 16px;
}
.item-list { padding: 0 12px; }
.cart-item {
  display: flex; align-items: center; gap: 10px; padding: 10px;
  background: var(--f7-list-bg-color, #f5f5f5); border-radius: 10px; margin-bottom: 6px;
}
.item-img { width: 48px; height: 48px; border-radius: 8px; overflow: hidden; flex-shrink: 0; background: var(--f7-surface-container, #e0e0e0); display: flex; align-items: center; justify-content: center; }
.item-img img { width: 100%; height: 100%; object-fit: cover; }
.item-body { flex: 1; min-width: 0; }
.item-name { font-size: 12px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.item-variant { font-size: 10px; color: var(--f7-on-surface-variant, #8e8e93); }
.item-price { font-size: 13px; font-weight: 500; color: var(--f7-theme-color); margin-top: 2px; }
.item-qty { flex-shrink: 0; }
.empty-hint { text-align: center; padding: 40px 16px; font-size: 12px; color: var(--f7-on-surface-variant, #8e8e93); }
.summary-block { background: var(--f7-list-bg-color, #f5f5f5); border-radius: 10px; padding: 12px 16px; margin: 8px 16px; }
.summary-row { display: flex; justify-content: space-between; font-size: 12.5px; padding: 4px 0; color: var(--f7-on-surface-variant, #8e8e93); }
.summary-total { display: flex; justify-content: space-between; font-size: 15px; font-weight: 500; padding-top: 8px; margin-top: 4px; border-top: 1px solid var(--f7-input-border-color, #e0e0e0); }
.section-title { font-size: 11px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; color: var(--f7-on-surface-variant, #8e8e93); padding: 12px 16px 6px; }
</style>
